<script>
/**
 * @component Turnos
 * @description Vista unificada de turnos del paciente. 2 tabs: "Mis turnos"
 *              (próximos + historial colapsable) y "Gestionar cita" (accesos
 *              directos desde historial + buscador general). Reemplaza
 *              PatientAppointments.vue y SacarTurno.vue.
 */
import { mapGetters } from "vuex";
import { format, addDays, isSameDay, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import appointmentService from "@/services/appointment";
import procedureService from "@/services/procedure";
import observationService, {
  indexByAppointment,
} from "@/services/appointment-observation";
import turneraService from "@/services/turnera";
import { APPOINTMENTS_TYPES } from "@/services/constants";
import {
  hidesAppointmentDoctor,
  appointmentInstitutionName,
  withoutHiddenDoctorAppointments,
  canCancelProcedureAppointment,
  procedureIdOf,
  procedureCancelErrorMessage,
  PROCEDURE_CANCEL_DIALOG,
} from "@/services/procedure-appointment";
import { resolveManageAppointment } from "@/services/patient-portal";
import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav";

/** @description Días a mostrar en la grilla de slots */
const DAYS_TO_SHOW = 4;

export default {
  name: "Turnos",
  components: { Spinner, BaseSubNav },

  data() {
    return {
      /** @description Turnos del paciente cargados */
      appointments: [],

      /**
       * @description UC-REG-275 — Observaciones que el consultorio marcó
       *              como visibles, por ID de turno (el API solo devuelve
       *              esas al paciente).
       */
      observations: {},

      /** @description Loading de turnos */
      isLoadingAppointments: false,

      /** @description Tab activa: 'turnos' | 'sacar' */
      activeTab: "turnos",

      /** @description Reloj para calcular disponibilidad virtual */
      now: new Date(),

      /** @description Intervalo del reloj */
      intervalId: null,

      /** @description Si el historial de turnos anteriores está expandido */
      pastExpanded: false,

      // ── Sección Gestionar cita ──

      /** @description Lista de especialidades */
      specialties: [],

      /** @description Especialidad seleccionada */
      selectedSpecialty: null,

      /** @description Ciudad seleccionada */
      selectedCity: "",

      /** @description Médicos encontrados en búsqueda */
      doctors: [],

      /** @description Loading de búsqueda */
      isSearching: false,

      /** @description Loading inicial de especialidades */
      isLoadingSearch: false,

      /** @description Paso actual del flujo: null | 'resultados' | 'turnos' | 'confirmacion' | 'exito' */
      searchStep: null,

      /** @description Médico seleccionado para ver grilla */
      selectedDoctor: null,

      /** @description Slots disponibles del médico seleccionado */
      slots: [],

      /** @description Loading de slots */
      isLoadingSlots: false,

      /** @description Día base de la grilla */
      gridStartDay: new Date(),

      /** @description Slot seleccionado para confirmar */
      selectedSlot: null,

      /** @description Form de confirmación */
      confirmForm: {
        modalidad: "presencial",
        motivo: "",
      },

      /** @description Loading de confirmación */
      isConfirming: false,

      /** @description Turno confirmado exitosamente */
      confirmedAppointment: null,

      /** @description Ciudades disponibles para filtrar */
      cities: [
        "Buenos Aires",
        "Córdoba",
        "Rosario",
        "Mendoza",
        "La Plata",
        "San Miguel de Tucumán",
        "Mar del Plata",
        "Salta",
        "Santa Fe",
        "San Juan",
      ],
    };
  },

  computed: {
    ...mapGetters(["isPatient", "user"]),

    /**
     * @description Turnos próximos (fecha >= hoy, no cancelados)
     * @returns {Array}
     */
    upcomingAppointments() {
      const today = this.startOfDay(this.now);
      return this.appointments
        .filter(
          (a) => new Date(a.startsAt) >= today && a.status !== "cancelled"
        )
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
    },

    /**
     * @description Turnos pasados o cancelados, ordenados descendente
     * @returns {Array}
     */
    pastAppointments() {
      const today = this.startOfDay(this.now);
      return this.appointments
        .filter((a) => new Date(a.startsAt) < today || a.status === "cancelled")
        .sort((a, b) => new Date(b.startsAt) - new Date(a.startsAt));
    },

    /**
     * @description Médicos únicos inferidos del historial de turnos
     * @returns {Array}
     */
    myDoctors() {
      const seen = new Set();
      const result = [];
      // UC-REG-262 — sin el prestador ficticio de los turnos de trámite.
      withoutHiddenDoctorAppointments(this.appointments).forEach((apt) => {
        const doctor = apt.doctor || apt.professional;
        if (!doctor) return;
        const id = doctor._id || doctor;
        if (seen.has(id)) return;
        seen.add(id);
        result.push({
          doctor,
          medicalSpecialty: apt.medicalSpecialty || null,
          lastVisit: apt.startsAt,
        });
      });
      return result.slice(0, 6);
    },

    /**
     * @description Días visibles en la grilla de slots
     * @returns {Array<Date>}
     */
    gridDays() {
      return Array.from({ length: DAYS_TO_SHOW }, (_, i) =>
        addDays(this.gridStartDay, i)
      );
    },

    /**
     * @description Slots agrupados por día para la grilla
     * @returns {Object}
     */
    slotsByDay() {
      const grouped = {};
      this.gridDays.forEach((day) => {
        const key = format(day, "yyyy-MM-dd");
        grouped[key] = this.slots
          .filter((s) => {
            const slotDate = parseISO(s.startsAt);
            return isSameDay(slotDate, day) && s.available === true;
          })
          .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
          .slice(0, 6);
      });
      return grouped;
    },

    /**
     * @description Si hay algún slot disponible en la grilla actual
     * @returns {boolean}
     */
    hasSlots() {
      return Object.values(this.slotsByDay).some((s) => s.length > 0);
    },
  },

  async created() {
    this.fetchAppointments();
    this.loadSpecialties();
    this.intervalId = setInterval(() => {
      this.now = new Date();
    }, 60000);
  },

  beforeDestroy() {
    if (this.intervalId) clearInterval(this.intervalId);
  },

  methods: {
    format,
    parseISO,

    // ── Turnos del paciente ──

    /**
     * @description Carga los turnos del paciente logueado
     */
    async fetchAppointments() {
      this.isLoadingAppointments = true;
      try {
        const data = await appointmentService.getMyAppointments(this.user._id);
        const raw = Array.isArray(data)
          ? data
          : data?.appointments || data?.docs || [];
        this.appointments = raw;
        this.observations = indexByAppointment(
          await observationService.getByAppointments(raw.map((a) => a._id))
        );
      } catch {
        this.appointments = [];
      } finally {
        this.isLoadingAppointments = false;
      }
    },

    /**
     * @description UC-REG-275 — Observación visible del turno, o "".
     * @param {Object} apt - Turno
     * @returns {string}
     */
    getObservation(apt) {
      const observation = this.observations[apt._id];
      return (observation && observation.text) || "";
    },

    /**
     * @description Cancela un turno con confirmación previa
     * @param {Object} appointment
     */
    cancelAppointment(appointment) {
      this.$confirm(
        "¿Estás seguro de que querés cancelar este turno?",
        "Cancelar turno",
        {
          confirmButtonText: "Sí, cancelar",
          cancelButtonText: "No",
          type: "warning",
        }
      )
        .then(() => {
          appointmentService
            .cancelAppointment(appointment._id)
            .then(() => {
              this.$message.success("Turno cancelado correctamente.");
              this.fetchAppointments();
            })
            .catch(() => {
              this.$message.error("No se pudo cancelar el turno.");
            });
        })
        .catch(() => {});
    },

    /**
     * @description UC-REG-300 — El turno de trámite se puede cancelar desde
     *              acá (flag de Campana; en los demás tenants sigue oculto).
     * @param {Object} apt
     * @returns {boolean}
     */
    canCancelProcedureAppointment(apt) {
      return canCancelProcedureAppointment(apt);
    },

    /**
     * @description UC-REG-300 — Cancela el turno de un trámite con motivo
     *              opcional, vía el endpoint del trámite (no el de agenda).
     * @param {Object} apt - Turno con `procedure`
     */
    cancelProcedureAppointment(apt) {
      this.$prompt(
        PROCEDURE_CANCEL_DIALOG.message,
        PROCEDURE_CANCEL_DIALOG.title,
        {
          confirmButtonText: PROCEDURE_CANCEL_DIALOG.confirm,
          cancelButtonText: PROCEDURE_CANCEL_DIALOG.cancel,
          inputPlaceholder: PROCEDURE_CANCEL_DIALOG.placeholder,
          type: "warning",
        }
      )
        .then(({ value }) =>
          procedureService
            .cancelAppointment(procedureIdOf(apt), value)
            .then(() => {
              this.$message.success(PROCEDURE_CANCEL_DIALOG.success);
              this.fetchAppointments();
            })
            .catch((err) => {
              this.$message.error(procedureCancelErrorMessage(err));
            })
        )
        .catch(() => {});
    },

    /**
     * @description Lleva al paciente a la sala de videollamada de su turno
     * (vista patient-video-room, no la pantalla de atención del médico).
     * @param {Object} appointment
     */
    joinVirtualAppointment(appointment) {
      this.$router.push({
        name: "patient-video-room",
        params: {
          appointmentId: appointment._id,
          patientId: this.user._id,
        },
      });
    },

    /**
     * @description Devuelve true si faltan 15 min o menos para el turno
     * @param {Object} appointment
     * @returns {boolean}
     */
    canJoinVirtual(appointment) {
      if (!this.isVirtual(appointment)) return false;
      const startsAt = new Date(appointment.startsAt);
      const diff = startsAt - this.now;
      return diff <= 15 * 60 * 1000;
    },

    /**
     * @description Indica si el turno es virtual. El campo del modelo es
     * `attentionType` ('in-person' | 'virtual') — no `type`.
     * @param {Object} appointment
     * @returns {boolean}
     */
    isVirtual(appointment) {
      return appointment.attentionType === "virtual";
    },

    /**
     * @description Nombre amigable del tipo de turno
     * @param {Object} appointment
     * @returns {string}
     */
    getTypeName(appointment) {
      const type = APPOINTMENTS_TYPES[appointment.attentionType];
      return type ? type.friendlyName : "Presencial";
    },

    /**
     * @description Clase CSS del badge de tipo
     * @param {Object} appointment
     * @returns {string}
     */
    getTypeClass(appointment) {
      return this.isVirtual(appointment) ? "tag--virtual" : "tag--in-person";
    },

    /**
     * @description Label del estado del turno
     * @param {Object} appointment
     * @returns {string}
     */
    getStatusLabel(appointment) {
      const map = {
        pending: "Pendiente",
        confirmed: "Confirmado",
        admitted: "Admitido",
        started: "En curso",
        done: "Atendido",
        cancelled: "Cancelado",
      };
      return map[appointment.status] || appointment.status;
    },

    /**
     * @description Clase CSS del badge de estado
     * @param {Object} appointment
     * @returns {string}
     */
    getStatusClass(appointment) {
      const map = {
        pending: "tag--pending",
        confirmed: "tag--confirmed",
        admitted: "tag--confirmed",
        started: "tag--confirmed",
        done: "tag--done",
        cancelled: "tag--cancelled",
      };
      return map[appointment.status] || "tag--pending";
    },

    /**
     * @description Nombre completo del médico del turno
     * @param {Object} appointment
     * @returns {string}
     */
    getDoctorName(appointment) {
      // UC-REG-262 — turno de trámite por institución: se ve la institución.
      if (hidesAppointmentDoctor(appointment)) {
        return appointmentInstitutionName(appointment);
      }
      const doctor = appointment.doctor || appointment.professional;
      if (!doctor) return "Sin asignar";
      return `${doctor.lastName || ""}, ${doctor.firstName || ""}`.trim();
    },

    /**
     * @description Nombre completo desde objeto doctor del historial
     * @param {Object} doctorObj
     * @returns {string}
     */
    getDoctorNameFromObj(doctorObj) {
      const d = doctorObj?.doctor || doctorObj;
      if (!d || typeof d === "string") return "Médico";
      const last = d.lastName || d.surname || "";
      const first = d.firstName || d.name || "";
      return `${last}${first ? ", " + first : ""}`.trim() || "Médico";
    },

    /**
     * @description Especialidad del médico del turno
     * @param {Object} appointment
     * @returns {string}
     */
    getSpecialty(appointment) {
      if (hidesAppointmentDoctor(appointment)) return "";
      const doctor = appointment.doctor || appointment.professional;
      if (!doctor) return "";
      return doctor.specialty || doctor.specialization || "";
    },

    /**
     * @description Día numérico de una fecha ISO
     * @param {string} dateStr
     * @returns {string}
     */
    getDay(dateStr) {
      return new Date(dateStr).getDate().toString().padStart(2, "0");
    },

    /**
     * @description Mes abreviado de una fecha ISO
     * @param {string} dateStr
     * @returns {string}
     */
    getMonth(dateStr) {
      const months = [
        "ENE",
        "FEB",
        "MAR",
        "ABR",
        "MAY",
        "JUN",
        "JUL",
        "AGO",
        "SEP",
        "OCT",
        "NOV",
        "DIC",
      ];
      return months[new Date(dateStr).getMonth()];
    },

    /**
     * @description Hora HH:mm de una fecha ISO
     * @param {string} dateStr
     * @returns {string}
     */
    getTime(dateStr) {
      const d = new Date(dateStr);
      return `${d.getHours().toString().padStart(2, "0")}:${d
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
    },

    /**
     * @description Inicio del día (00:00:00)
     * @param {Date} date
     * @returns {Date}
     */
    startOfDay(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    },

    // ── Sección Gestionar cita ──

    /**
     * @description Carga especialidades para el buscador
     */
    async loadSpecialties() {
      this.isLoadingSearch = true;
      try {
        const res = await turneraService.getSpecialties();
        this.specialties = res || [];
      } catch (err) {
        console.error("Error cargando especialidades:", err);
      } finally {
        this.isLoadingSearch = false;
      }
    },

    /**
     * @description Busca médicos según especialidad y ciudad
     */
    async searchDoctors() {
      if (!this.selectedSpecialty) {
        this.$message.warning("Seleccioná una especialidad");
        return;
      }
      this.isSearching = true;
      this.doctors = [];
      try {
        const res = await turneraService.getDoctors({
          medicalSpecialties: this.selectedSpecialty._id,
          city: this.selectedCity || undefined,
        });
        this.doctors = this.mergeAgendas(res?.docs || []);
        this.searchStep = "resultados";
      } catch (err) {
        this.$message.error("Error al buscar profesionales");
      } finally {
        this.isSearching = false;
      }
    },

    /**
     * @description Agrupa agendas por médico evitando duplicados
     * @param {Array} agendas
     * @returns {Array}
     */
    mergeAgendas(agendas) {
      const map = {};
      agendas.forEach((agenda) => {
        const doctorId = agenda.doctor?._id || agenda.doctor;
        if (!doctorId) return;
        if (!map[doctorId]) {
          map[doctorId] = { ...agenda, agendaIds: [agenda._id] };
        } else {
          map[doctorId].agendaIds.push(agenda._id);
        }
      });
      return Object.values(map);
    },

    /**
     * @description Selecciona un médico y carga sus slots disponibles
     * @param {Object} doctor
     */
    async selectDoctor(doctor) {
      this.selectedDoctor = doctor;
      this.slots = [];
      this.isLoadingSlots = true;
      this.searchStep = "turnos";
      try {
        /** @description El endpoint espera el _id del usuario doctor, no de la agenda */
        const doctorId = doctor.doctor?._id || doctor.doctor;
        const res = await turneraService.getSlotsByDoctor(doctorId);
        this.slots = res?.docs || res || [];
        /** @description Mueve la grilla al primer día con turnos disponibles */
        const firstSlot = this.slots
          .filter((s) => s.available && new Date(s.startsAt) >= new Date())
          .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))[0];
        if (firstSlot) {
          this.gridStartDay = parseISO(firstSlot.startsAt);
        } else {
          this.gridStartDay = new Date();
        }
      } catch (err) {
        this.$message.error("Error al cargar turnos disponibles");
      } finally {
        this.isLoadingSlots = false;
      }
    },

    /**
     * @description UC-REG-306 — "Gestionar cita" (pestaña y botón del estado
     *              vacío). En Campana lleva a "Mis trámites" (los turnos se
     *              asignan sobre un trámite); en el resto abre el buscador.
     */
    openManageAppointment() {
      const { route, tab } = resolveManageAppointment();
      if (route) {
        this.$router.push(route);
        return;
      }
      this.activeTab = tab;
    },

    /**
     * @description Acceso directo desde "Mis médicos" — cambia tab y va a slots
     * @param {Object} myDoctor - { doctor, medicalSpecialty, lastVisit }
     */
    quickBookDoctor(myDoctor) {
      const docObj = {
        doctor: myDoctor.doctor,
        medicalSpecialties: myDoctor.medicalSpecialty
          ? [myDoctor.medicalSpecialty]
          : [],
      };
      this.activeTab = "sacar";
      this.selectDoctor(docObj);
    },

    /**
     * @description Vuelve a la lista de resultados desde la grilla
     */
    backToResults() {
      this.searchStep = "resultados";
    },

    /**
     * @description Selecciona un slot y avanza a confirmación
     * @param {Object} slot
     */
    selectSlot(slot) {
      this.selectedSlot = slot;
      this.$nextTick(() => {
        this.searchStep = "confirmacion";
      });
    },

    /**
     * @description Navega en la grilla de slots
     * @param {number} dir - 1 adelante, -1 atrás
     */
    navigateGrid(dir) {
      this.gridStartDay = addDays(this.gridStartDay, dir * DAYS_TO_SHOW);
    },

    /**
     * @description Confirma el turno seleccionado
     */
    async confirmAppointment() {
      if (!this.selectedSlot) return;
      this.isConfirming = true;
      try {
        /** @description Payload para POST /appointments */
        const payload = {
          slot: this.selectedSlot._id,
          patient: this.user._id,
          attentionType:
            this.confirmForm.modalidad === "virtual" ? "virtual" : "in-person",
          reason: this.confirmForm.motivo,
          institution: this.selectedSlot.institution,
          medicalSpecialty: this.selectedSlot.medicalSpecialties?.[0],
        };
        const res = await turneraService.createAppointment(payload);
        this.confirmedAppointment = res;
        this.searchStep = "exito";
        /** @description Recargamos turnos para reflejar el nuevo en "Mis turnos" */
        this.fetchAppointments();
      } catch (err) {
        this.$message.error("Error al confirmar el turno");
      } finally {
        this.isConfirming = false;
      }
    },

    /**
     * @description Reinicia el flujo de búsqueda
     */
    resetSearch() {
      this.searchStep = null;
      this.selectedDoctor = null;
      this.selectedSlot = null;
      this.confirmedAppointment = null;
      this.doctors = [];
      this.slots = [];
      this.confirmForm = { modalidad: "presencial", motivo: "" };
    },

    /**
     * @description Hora del slot formateada HH:mm
     * @param {Object} slot
     * @returns {string}
     */
    formatSlotTime(slot) {
      if (!slot?.startsAt) return "";
      return format(parseISO(slot.startsAt), "HH:mm");
    },

    /**
     * @description Encabezado de día para la grilla
     * @param {Date} date
     * @returns {{ label: string, day: string }}
     */
    formatGridDay(date) {
      const isToday = isSameDay(date, new Date());
      return {
        label: isToday
          ? "HOY"
          : format(date, "EEE.", { locale: es }).toUpperCase(),
        day: format(date, "d MMM", { locale: es }),
      };
    },

    /**
     * @description Wrapper de format para usar en template
     * @param {Date} date
     * @param {string} fmt
     * @returns {string}
     */
    formatDate(date, fmt) {
      return format(date, fmt);
    },

    /**
     * @description Nombre completo del médico en búsqueda
     * @param {Object} doctor
     * @returns {string}
     */
    doctorName(doctor) {
      const d = doctor?.doctor || doctor;
      if (!d) return "—";
      const last = d.lastName || d.surname || "";
      const first = d.firstName || d.name || "";
      return `Dr. ${last}${first ? ", " + first : ""}`.trim();
    },

    /**
     * @description Dirección de la institución del médico
     * @param {Object} doctor
     * @returns {string}
     */
    doctorAddress(doctor) {
      const addr = doctor?.institution?.address;
      if (!addr) return "";
      if (typeof addr === "string") return addr;
      const { street, buildingNumber, city } = addr;
      return [street, buildingNumber, city].filter(Boolean).join(" ");
    },

    /**
     * @description Especialidad del médico en búsqueda
     * @param {Object} doctor
     * @returns {string}
     */
    doctorSpecialty(doctor) {
      return (
        doctor?.medicalSpecialties?.[0]?.name ||
        this.selectedSpecialty?.name ||
        "—"
      );
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(:showNavigation="true")
    template(#navigation)
      a.page-toolbar__tab(
        :class="{ 'page-toolbar__tab--active': activeTab === 'turnos' }"
        @click="activeTab = 'turnos'"
      )
        | Mis turnos
        span.tv-tab-badge(v-if="upcomingAppointments.length") {{ upcomingAppointments.length }}
      a.page-toolbar__tab(
        :class="{ 'page-toolbar__tab--active': activeTab === 'sacar' }"
        @click="openManageAppointment"
      ) Gestionar cita

  .turnos-view

    //- ══════════════════════════════════════════
    //- TAB: MIS TURNOS
    //- ══════════════════════════════════════════
    template(v-if="activeTab === 'turnos'")

      //- Próximos
      .tv-section
        .tv-section__header
          h2.tv-section__title Próximos turnos
          span.tv-badge(v-if="upcomingAppointments.length") {{ upcomingAppointments.length }}

        div(v-loading="isLoadingAppointments")
          .tv-empty-state(v-if="!isLoadingAppointments && upcomingAppointments.length === 0")
            p.tv-empty-state__text No tenés turnos próximos
            button.tv-btn-primary(@click="openManageAppointment") Gestionar cita

          .tv-apt-card(
            v-for="apt in upcomingAppointments"
            :key="apt._id"
          )
            .tv-apt-card__date
              span.tv-apt-card__day {{ getDay(apt.startsAt) }}
              span.tv-apt-card__month {{ getMonth(apt.startsAt) }}
              span.tv-apt-card__time {{ getTime(apt.startsAt) }}
            .tv-apt-card__info
              span.tv-apt-card__doctor {{ getDoctorName(apt) }}
              span.tv-apt-card__specialty(v-if="getSpecialty(apt)") {{ getSpecialty(apt) }}
              .tv-apt-card__tags
                span.tv-tag(:class="getTypeClass(apt)") {{ getTypeName(apt) }}
                span.tv-tag(:class="getStatusClass(apt)") {{ getStatusLabel(apt) }}
                //- UC-REG-253 — turno asignado desde un trámite (sin agenda).
                span.tv-tag.tag--procedure(v-if="apt.procedure") Por trámite
              span.tv-apt-card__reason(v-if="apt.reason") {{ apt.reason }}
              span.tv-apt-card__reason(v-else-if="apt.procedure && canCancelProcedureAppointment(apt)") Para reprogramar, respondé en "Mis trámites".
              span.tv-apt-card__reason(v-else-if="apt.procedure") Para cambios, respondé en "Mis trámites".
              //- UC-REG-275 — observación del consultorio (solo si es visible)
              span.tv-apt-card__note(v-if="getObservation(apt)")
                i.el-icon-chat-line-square
                |  {{ getObservation(apt) }}
            .tv-apt-card__actions
              el-button.tv-btn-cancel(v-if="!apt.procedure" size="small" @click="cancelAppointment(apt)") Cancelar
              //- UC-REG-300 — turno de trámite cancelable (flag de Campana).
              el-button.tv-btn-cancel(v-else-if="canCancelProcedureAppointment(apt)" size="small" @click="cancelProcedureAppointment(apt)") Cancelar
              el-button.tv-btn-join(
                v-if="isVirtual(apt)"
                size="small"
                :disabled="!canJoinVirtual(apt)"
                @click="joinVirtualAppointment(apt)"
              ) Ingresar

      //- Historial colapsable
      .tv-section(v-if="pastAppointments.length > 0")
        .tv-past-toggle(@click="pastExpanded = !pastExpanded")
          h2.tv-section__title Anteriores
          span.tv-badge.tv-badge--gray {{ pastAppointments.length }}
          span.tv-past-toggle__chevron {{ pastExpanded ? '▾' : '▸' }}

        template(v-if="pastExpanded")
          .tv-apt-card.tv-apt-card--past(
            v-for="apt in pastAppointments"
            :key="apt._id"
            :class="{ 'tv-apt-card--cancelled': apt.status === 'cancelled' }"
          )
            .tv-apt-card__date.tv-apt-card__date--past(
              :class="{ 'tv-apt-card__date--cancelled': apt.status === 'cancelled' }"
            )
              span.tv-apt-card__day {{ getDay(apt.startsAt) }}
              span.tv-apt-card__month {{ getMonth(apt.startsAt) }}
              span.tv-apt-card__time {{ getTime(apt.startsAt) }}
            .tv-apt-card__info
              span.tv-apt-card__doctor(
                :class="{ 'tv-apt-card__doctor--cancelled': apt.status === 'cancelled' }"
              ) {{ getDoctorName(apt) }}
              span.tv-apt-card__specialty(v-if="getSpecialty(apt)") {{ getSpecialty(apt) }}
              .tv-apt-card__tags
                span.tv-tag(:class="getTypeClass(apt)") {{ getTypeName(apt) }}
                span.tv-tag(:class="getStatusClass(apt)") {{ getStatusLabel(apt) }}
              span.tv-apt-card__note(v-if="getObservation(apt)")
                i.el-icon-chat-line-square
                |  {{ getObservation(apt) }}

    //- ══════════════════════════════════════════
    //- TAB: SACAR TURNO
    //- ══════════════════════════════════════════
    template(v-if="activeTab === 'sacar'")

      //- Accesos directos: mis médicos del historial
      .tv-section(v-if="myDoctors.length > 0 && !searchStep")
        .tv-section__header
          h2.tv-section__title Mis médicos
        .tv-quick-doctors
          .tv-quick-doc(
            v-for="(item, i) in myDoctors"
            :key="i"
            @click="quickBookDoctor(item)"
          )
            .tv-quick-doc__avatar {{ getDoctorNameFromObj(item).charAt(0).toUpperCase() }}
            .tv-quick-doc__info
              p.tv-quick-doc__name {{ getDoctorNameFromObj(item) }}
              p.tv-quick-doc__spec
                | {{ item.medicalSpecialty?.name || '—' }}
                span.tv-quick-doc__last(v-if="item.lastVisit")  · últ. {{ getDay(item.lastVisit) }} {{ getMonth(item.lastVisit) }}
            span.tv-quick-doc__arrow →

      //- Buscador general
      .tv-section(v-if="!searchStep || searchStep === 'resultados'")
        .tv-section__header
          h2.tv-section__title(v-if="myDoctors.length > 0") Buscar otro médico
          h2.tv-section__title(v-else) Buscar médico

        .tv-search-card
          .tv-search-row
            el-select.tv-search-row__specialty(
              v-model="selectedSpecialty"
              placeholder="Especialidad"
              filterable
              value-key="_id"
            )
              el-option(
                v-for="s in specialties"
                :key="s._id"
                :label="s.name"
                :value="s"
              )
            el-select.tv-search-row__city(
              v-model="selectedCity"
              placeholder="Ciudad"
              clearable
            )
              el-option(
                v-for="c in cities"
                :key="c"
                :label="c"
                :value="c"
              )
            button.tv-btn-search(
              @click="searchDoctors"
              :disabled="isSearching || !selectedSpecialty"
            ) {{ isSearching ? 'Buscando...' : 'Buscar' }}

          //- Resultados
          template(v-if="searchStep === 'resultados'")
            .tv-results-header
              span.tv-results-count {{ doctors.length }} profesionales encontrados
            .tv-spinner-wrap(v-if="isSearching")
              i.el-icon-loading
            .tv-doctor-list(v-else)
              .tv-result-card(
                v-for="(doc, i) in doctors"
                :key="i"
                @click="selectDoctor(doc)"
              )
                .tv-result-card__avatar
                  i.el-icon-user
                .tv-result-card__info
                  p.tv-result-card__name {{ doctorName(doc) }}
                  p.tv-result-card__spec {{ doctorSpecialty(doc) }}
                  p.tv-result-card__addr(v-if="doc.institution?.address") {{ doctorAddress(doc) }}
                .tv-result-card__prices
                  span.tv-price-chip.presencial(v-if="doc.inPersonValue") Presencial ${{ doc.inPersonValue.toLocaleString() }}
                  span.tv-price-chip.virtual(v-if="doc.virtualValue") Virtual ${{ doc.virtualValue.toLocaleString() }}
              .tv-empty-inline(v-if="doctors.length === 0")
                i.el-icon-search
                span No se encontraron profesionales para esta especialidad

      //- Grilla de slots
      .tv-section(v-if="searchStep === 'turnos'")
        .tv-search-card
          .tv-slots-header
            button.tv-btn-back(@click="backToResults")
              i.el-icon-arrow-left
              | Volver
            .tv-slots-doctor
              .tv-slots-doctor__avatar
                i.el-icon-user
              .tv-slots-doctor__info
                p.tv-slots-doctor__name {{ doctorName(selectedDoctor) }}
                p.tv-slots-doctor__spec {{ doctorSpecialty(selectedDoctor) }}

          .tv-spinner-wrap(v-if="isLoadingSlots")
            i.el-icon-loading
            p Cargando turnos disponibles...

          template(v-else)
            .tv-grid-nav
              button.tv-grid-nav__btn(
                @click="navigateGrid(-1)"
                :disabled="gridStartDay <= new Date()"
              )
                i.el-icon-arrow-left
              .tv-grid
                .tv-grid__col(
                  v-for="(day, di) in gridDays"
                  :key="di"
                )
                  .tv-grid__day-header
                    span.tv-grid__day-label {{ formatGridDay(day).label }}
                    span.tv-grid__day-date {{ formatGridDay(day).day }}
                  .tv-slot(
                    v-for="slot in slotsByDay[formatDate(day, 'yyyy-MM-dd')]"
                    :key="slot._id"
                    @click="selectSlot(slot)"
                  ) {{ formatSlotTime(slot) }}
                  .tv-no-slots(v-if="!slotsByDay[formatDate(day, 'yyyy-MM-dd')]?.length") —
              button.tv-grid-nav__btn(@click="navigateGrid(1)")
                i.el-icon-arrow-right

            .tv-empty-inline(v-if="!hasSlots && !isLoadingSlots")
              span No hay turnos disponibles en este período

      //- Confirmación
      .tv-section(v-if="searchStep === 'confirmacion'")
        .tv-search-card
          .tv-confirm
            button.tv-btn-back(@click="searchStep = 'turnos'")
              i.el-icon-arrow-left
              | Volver

            h3.tv-confirm__title Confirmá tu turno

            .tv-confirm__summary
              .tv-confirm__doctor-row
                .tv-confirm__avatar
                  i.el-icon-user
                .tv-confirm__doctor-info
                  p.tv-confirm__doctor-name {{ doctorName(selectedDoctor) }}
                  p.tv-confirm__doctor-spec {{ doctorSpecialty(selectedDoctor) }}
              .tv-confirm__detail-row
                span.tv-confirm__label Fecha y hora
                span.tv-confirm__value(v-if="selectedSlot")
                  | {{ formatDate(parseISO(selectedSlot.startsAt), "d 'de' MMMM 'a las' HH:mm") }}
              .tv-confirm__detail-row(v-if="selectedDoctor?.institution?.address")
                span.tv-confirm__label Dirección
                span.tv-confirm__value {{ doctorAddress(selectedDoctor) }}

            .tv-confirm__form
              .tv-confirm__field
                label.tv-confirm__field-label Modalidad
                .tv-confirm__radio-group
                  label.tv-confirm__radio
                    input(type="radio" v-model="confirmForm.modalidad" value="presencial")
                    span Presencial
                  label.tv-confirm__radio
                    input(type="radio" v-model="confirmForm.modalidad" value="virtual")
                    span Virtual
              .tv-confirm__field
                label.tv-confirm__field-label Motivo de consulta (opcional)
                el-input(
                  v-model="confirmForm.motivo"
                  type="textarea"
                  :rows="3"
                  placeholder="Describí brevemente el motivo..."
                )

            button.tv-btn-primary.wide(
              @click="confirmAppointment"
              :disabled="isConfirming"
            ) {{ isConfirming ? 'Confirmando...' : 'Confirmar turno' }}

      //- Éxito
      .tv-section(v-if="searchStep === 'exito'")
        .tv-search-card
          .tv-success
            .tv-success__icon
              i.el-icon-circle-check
            h3.tv-success__title ¡Turno confirmado!
            p.tv-success__sub Tu turno fue agendado correctamente.
            .tv-success__detail(v-if="confirmedAppointment && selectedSlot")
              p {{ doctorName(selectedDoctor) }}
              p {{ formatDate(parseISO(selectedSlot.startsAt), "d 'de' MMMM 'a las' HH:mm") }}
            .tv-success__actions
              button.tv-btn-primary(@click="resetSearch") Sacar otro turno
              button.tv-btn-secondary(@click="activeTab = 'turnos'; resetSearch()") Ver mis turnos
</template>

<style lang="scss" scoped>
$navy: #002d5e;
$teal: #0d9d9d;
$teal-bg: rgba(13, 157, 157, 0.06);
$gray-50: #f8fafc;
$gray-100: #f1f5f9;
$gray-200: #e2e8f0;
$gray-300: #cbd5e1;
$gray-400: #94a3b8;
$gray-500: #64748b;

.turnos-view {
  padding: 20px 28px;
  font-family: "Plus Jakarta Sans", sans-serif;
  max-width: 900px;
}

.tv-section {
  margin-bottom: 24px;

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  &__title {
    font-size: 11px;
    font-weight: 700;
    color: $gray-400;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
}

.tv-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  background: $teal;
  color: white;

  &--gray {
    background: $gray-200;
    color: $gray-500;
  }
}

.tv-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  background: #1d9e75;
  color: white;
  margin-left: 5px;
}

.tv-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px;
  background: white;
  border: 1.5px dashed $gray-200;
  border-radius: 10px;
  text-align: center;
  gap: 14px;

  &__text {
    font-size: 13px;
    color: $gray-400;
    margin: 0;
  }
}

.tv-apt-card {
  display: flex;
  align-items: center;
  background: white;
  border: 1.5px solid $gray-200;
  border-radius: 10px;
  margin-bottom: 8px;
  overflow: hidden;
  transition: border-color 0.15s;

  &:hover {
    border-color: $gray-300;
  }
  &--past {
    opacity: 0.8;
  }
  &--cancelled {
    opacity: 0.55;
  }

  &__date {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 64px;
    align-self: stretch;
    background: $navy;
    padding: 10px 8px;
    gap: 2px;

    &--past {
      background: $gray-200;
    }
    &--cancelled {
      background: $gray-300;
    }
  }

  &__day {
    font-size: 22px;
    font-weight: 700;
    color: white;
    line-height: 1;
  }
  &__month {
    font-size: 9px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 0.5px;
  }
  &__time {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 2px;
  }

  &__date--past &__day,
  &__date--past &__month,
  &__date--past &__time {
    color: $gray-500;
  }

  &__info {
    flex: 1;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  &__doctor {
    font-size: 13px;
    font-weight: 700;
    color: $navy;

    &--cancelled {
      text-decoration: line-through;
      color: $gray-400;
    }
  }

  &__specialty {
    font-size: 11px;
    color: $gray-400;
  }
  &__tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 4px;
  }
  &__reason {
    font-size: 11px;
    color: $gray-400;
  }
  // UC-REG-275 — observación del consultorio
  &__note {
    font-size: 12px;
    color: #475569;
    white-space: pre-wrap;
    word-break: break-word;
  }
  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    flex-shrink: 0;
  }
}

.tv-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}
.tag--in-person {
  background: #dbeafe;
  color: #1e40af;
}
.tag--virtual {
  background: #d1fae5;
  color: #065f46;
}
// UC-REG-253 — turno creado desde un trámite (sin agenda ni slot).
.tag--procedure {
  background: #ede9fe;
  color: #5b21b6;
}
.tag--confirmed {
  background: #d1fae5;
  color: #065f46;
}
.tag--pending {
  background: #fef3c7;
  color: #92400e;
}
.tag--done {
  background: #ccfbf1;
  color: #0d9d9d;
}
.tag--cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.tv-btn-cancel {
  border: 1px solid $gray-200;
  color: $gray-500;
  background: white;
  font-size: 12px;
  &:hover {
    color: #dc2626;
    border-color: #dc2626;
    background: #fef2f2;
  }
}

.tv-btn-join {
  background: $teal;
  color: white;
  border: none;
  font-size: 12px;
  font-weight: 600;
  &:hover:not(:disabled) {
    background: #0b8a8a;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.tv-past-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 12px 14px;
  background: white;
  border: 1.5px solid $gray-200;
  border-radius: 10px;
  user-select: none;
  transition: border-color 0.15s;

  &:hover {
    border-color: $gray-300;
  }
  &__chevron {
    font-size: 12px;
    color: $gray-400;
    margin-left: auto;
  }

  & + .tv-apt-card {
    margin-top: 12px;
  }
}

.tv-quick-doctors {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tv-quick-doc {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1.5px solid $gray-200;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #1d9e75;
    background: #e1f5ee;
  }

  &__avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: $teal-bg;
    border: 1px solid rgba($teal, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 700;
    color: $teal;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
  }
  &__name {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    margin: 0 0 2px;
  }
  &__spec {
    font-size: 11px;
    color: $gray-400;
    margin: 0;
  }
  &__last {
    color: $gray-300;
  }
  &__arrow {
    font-size: 16px;
    color: #1d9e75;
    flex-shrink: 0;
  }
}

.tv-search-card {
  background: white;
  border: 1.5px solid $gray-200;
  border-radius: 12px;
  padding: 20px;
}

.tv-search-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  &__specialty {
    flex: 1;
    min-width: 180px;
  }
  &__city {
    flex: 1;
    min-width: 140px;
  }
}

.tv-btn-search {
  background: $navy;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: darken($navy, 8%);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.tv-results-header {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid $gray-100;
}

.tv-results-count {
  font-size: 11px;
  color: $gray-400;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tv-doctor-list {
  margin-top: 10px;
}

.tv-result-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1.5px solid $gray-200;
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.15s;

  &:hover {
    border-color: $teal;
    background: $teal-bg;
  }

  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $gray-100;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: $navy;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
  }
  &__name {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    margin: 0 0 2px;
  }
  &__spec,
  &__addr {
    font-size: 11px;
    color: $gray-400;
    margin: 0 0 2px;
  }
  &__prices {
    display: flex;
    gap: 6px;
    flex-direction: column;
    align-items: flex-end;
  }
}

.tv-price-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;

  &.presencial {
    background: #e1eaf6;
    color: $navy;
  }
  &.virtual {
    background: $teal-bg;
    color: $teal;
    border: 1px solid rgba($teal, 0.3);
  }
}

.tv-slots-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.tv-slots-doctor {
  display: flex;
  align-items: center;
  gap: 10px;

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: $gray-100;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: $navy;
  }

  &__name {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }
  &__spec {
    font-size: 11px;
    color: $gray-400;
    margin: 0;
  }
}

.tv-grid-nav {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  &__btn {
    background: none;
    border: 1.5px solid $gray-200;
    border-radius: 8px;
    padding: 8px;
    cursor: pointer;
    margin-top: 42px;
    color: $gray-400;
    transition: all 0.15s;

    &:hover:not(:disabled) {
      border-color: $teal;
      color: $teal;
    }
    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }
}

.tv-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;

  &__col {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__day-header {
    text-align: center;
    margin-bottom: 4px;
  }
  &__day-label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    color: $teal;
    letter-spacing: 0.5px;
  }
  &__day-date {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: $navy;
  }
}

.tv-slot {
  background: $navy;
  color: white;
  border-radius: 8px;
  padding: 8px 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: darken($navy, 5%);
    transform: scale(1.03);
  }
}

.tv-no-slots {
  text-align: center;
  font-size: 12px;
  color: $gray-300;
  padding: 8px 0;
}

.tv-confirm {
  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $navy;
    margin: 16px 0;
  }

  &__summary {
    background: $gray-50;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 20px;
  }

  &__doctor-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
  }

  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $gray-100;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: $navy;
  }

  &__doctor-name {
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    margin: 0;
  }
  &__doctor-spec {
    font-size: 11px;
    color: $gray-400;
    margin: 0;
  }

  &__detail-row {
    display: flex;
    gap: 8px;
    align-items: baseline;
    padding: 5px 0;
    border-top: 1px solid $gray-100;
  }
  &__label {
    font-size: 11px;
    color: $gray-400;
    min-width: 80px;
  }
  &__value {
    font-size: 13px;
    color: $navy;
    font-weight: 600;
  }
  &__form {
    margin-bottom: 20px;
  }
  &__field {
    margin-bottom: 14px;
  }

  &__field-label {
    display: block;
    font-size: 11px;
    font-weight: 700;
    color: $gray-500;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 8px;
  }

  &__radio-group {
    display: flex;
    gap: 16px;
  }
  &__radio {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: $navy;
    cursor: pointer;
  }
}

.tv-success {
  text-align: center;
  padding: 40px 20px;

  &__icon {
    font-size: 48px;
    color: $teal;
    margin-bottom: 12px;
  }
  &__title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    margin-bottom: 6px;
  }
  &__sub {
    font-size: 13px;
    color: $gray-400;
    margin-bottom: 20px;
  }

  &__detail {
    font-size: 13px;
    color: $navy;
    font-weight: 600;
    margin-bottom: 24px;
    p {
      margin: 2px 0;
    }
  }

  &__actions {
    display: flex;
    gap: 10px;
    justify-content: center;
  }
}

.tv-btn-primary {
  background: $navy;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: darken($navy, 8%);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
  &.wide {
    width: 100%;
    padding: 11px;
    font-size: 14px;
  }
}

.tv-btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 20px;
  border: 1.5px solid $gray-200;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: $navy;
  background: white;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;

  &:hover {
    border-color: $navy;
  }
}

.tv-btn-back {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: 12px;
  color: $gray-400;
  cursor: pointer;
  font-family: inherit;
  padding: 0;

  &:hover {
    color: $navy;
  }
}

.tv-empty-inline {
  padding: 16px 0;
  font-size: 13px;
  color: $gray-400;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 18px;
  }
}

.tv-spinner-wrap {
  text-align: center;
  padding: 40px;
  color: $gray-400;
  font-size: 13px;

  i {
    font-size: 24px;
    display: block;
    margin-bottom: 8px;
  }
}
</style>
